import { useState } from 'react';
import type { SectionGate } from '@kevlarcon/pages';
import { partnersApi } from '../../api/partners';
import { useAuth } from '../../contexts/AuthContext';

/**
 * The `partner` access gate (see tenant_site/system_pages.py's PartnerJoinPage
 * and partners/apps.py's `partner` rule) — what the Partner-With-Us page shows
 * in place of its form until the visitor is signed in as a school's partner
 * account. Registered under `accessGates.partner` in SitePagesProvider.
 *
 * `unauthenticated`: asks for an email and has the server make (or find) that
 * address's partner login and mail a link back to this page; clicking it
 * signs the school in and the page then serves the real form.
 * `forbidden`: signed in, but not as a partner (an ordinary member of the
 * organization, say) — offer to sign out so they can continue as a school.
 */
/** The site's section frame (`.states .head` is what styles the heading block). */
function GateFrame({
  heading, lede, testId, children,
}: { heading: string; lede?: string; testId: string; children?: React.ReactNode }) {
  return (
    <section className="states section partner-gate" data-testid={testId}>
      <div className="wrap">
        <div className="head">
          <h2>{heading}</h2>
          {lede && <p>{lede}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

export default function PartnerJoinGate({ state }: SectionGate) {
  const { logout } = useAuth();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const form = await partnersApi.getPublicForm();
      await partnersApi.startJoin(form.id, email);
      setSentTo(email.trim());
    } catch (err: any) {
      setError(
        err.response?.data?.error
          || (err.response?.status === 429
            ? 'Too many attempts. Please wait a minute and try again.'
            : 'Something went wrong. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  if (state === 'forbidden') {
    return (
      <GateFrame
        heading="You're signed in with a different kind of account"
        lede="This page is for school partner accounts. Sign out to continue with your school's email."
        testId="partner-join-gate-forbidden"
      >
        <div className="partner-gate-fields">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => logout()}
            data-testid="partner-join-gate-logout-button"
          >
            Sign out
          </button>
        </div>
      </GateFrame>
    );
  }

  if (sentTo) {
    return (
      <GateFrame heading="Check your email" testId="partner-join-gate-sent">
        <p className="partner-gate-fields" data-testid="partner-join-gate-sent-message">
          We sent a link to <strong>{sentTo}</strong>. Open it on this device to
          continue your listing. It expires shortly.
        </p>
      </GateFrame>
    );
  }

  return (
    <GateFrame
      heading="Start with your school's email"
      lede="We'll email you a sign-in link. That same email is how you'll come back to keep your listing up to date."
      testId="partner-join-gate-form"
    >
      <form className="partner-gate-fields" onSubmit={submit}>
        <label htmlFor="partner-join-gate-email">School contact email</label>
        <input
          id="partner-join-gate-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="partner-join-gate-email-input"
        />
        {error && <p role="alert" data-testid="partner-join-gate-error-message">{error}</p>}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
          data-testid="partner-join-gate-submit-button"
        >
          {loading ? 'Sending…' : 'Email me a link'}
        </button>
      </form>
    </GateFrame>
  );
}
